import { z } from 'zod';
import { assumptionValueSchema } from './value.js';
import { ASSUMPTION_CONTENT_SCHEMA_VERSION, assumptionDefinition, assumptionDefinitionFor, assumptionDefinitions, parseIndexKey, parseTemplateKey, } from './registry.js';
import { resolveEconomy } from './indices.js';
/**
 * The CONTENT of one assumption-set version (spec §10.3) and the validation
 * that decides whether it may be published or run.
 *
 * Two different strictness levels, on purpose:
 *  - the zod schema accepts any well-formed draft, including an incomplete one
 *    (AC-03: a blank draft saves);
 *  - `validateAssumptionContent` reports what stops that draft being PUBLISHED
 *    or SELECTED — missing required values, unknown or gated keys, wrong value
 *    kind for the definition, out-of-bounds figures, reference cycles.
 */
/** §10.4 operational limits. */
export const MAX_FIELDS_PER_VERSION = 5000;
export const MAX_PATH_SEGMENTS_PER_VERSION = 1000;
export const MAX_EVIDENCE_ENTRIES = 50;
const uuid = z.string().uuid();
export const assumptionEvidenceSchema = z
    .object({
    kind: z.enum(['internalRationale', 'publicSource', 'clientExplanation']),
    text: z.string().trim().min(1).max(2000),
    /** Stored as a link only; the server never fetches it (§11). */
    url: z
        .string()
        .url()
        .startsWith('https://', 'Evidence links must be https')
        .max(500)
        .optional(),
    sourceDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /** `internal` evidence never reaches a client output (§12). */
    visibility: z.enum(['internal', 'client']),
})
    .strict()
    .refine((e) => e.kind !== 'internalRationale' || e.visibility === 'internal', {
    message: 'Internal rationale cannot be marked client-visible',
    path: ['visibility'],
});
export const profileVersionRefSchema = z
    .object({ profileId: uuid, versionId: uuid, origin: z.enum(['firm', 'product']) })
    .strict();
export const assumptionSetContentSchema = z
    .object({
    schemaVersion: z.literal(ASSUMPTION_CONTENT_SCHEMA_VERSION),
    /** The published version this content inherits from; null = standalone. */
    baseVersionId: uuid.nullable(),
    /** Absent key = inherit. Present key = explicit, including explicit zero. */
    values: z.record(z.string().min(1).max(160), assumptionValueSchema),
    profileVersions: z.array(profileVersionRefSchema).max(200).default([]),
    evidence: z.array(assumptionEvidenceSchema).max(MAX_EVIDENCE_ENTRIES).default([]),
})
    .strict()
    .superRefine((c, ctx) => {
    const keys = Object.keys(c.values);
    if (keys.length > MAX_FIELDS_PER_VERSION) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['values'],
            message: `A version can hold at most ${MAX_FIELDS_PER_VERSION} fields`,
        });
    }
    let segments = 0;
    for (const k of keys) {
        const v = c.values[k];
        if (v.kind === 'path')
            segments += v.segments.length;
    }
    if (segments > MAX_PATH_SEGMENTS_PER_VERSION) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['values'],
            message: `A version can hold at most ${MAX_PATH_SEGMENTS_PER_VERSION} path segments in total`,
        });
    }
});
export function emptyAssumptionContent(baseVersionId = null) {
    return {
        schemaVersion: ASSUMPTION_CONTENT_SCHEMA_VERSION,
        baseVersionId,
        values: {},
        profileVersions: [],
        evidence: [],
    };
}
// --- canonical serialisation (§10.3) -----------------------------------------
/** Versioned so a future canonicalisation change cannot silently re-hash history. */
export const ASSUMPTION_CANONICALISATION_VERSION = 'cf-assumptions-canon-1';
function canonicalise(node) {
    if (Array.isArray(node))
        return node.map(canonicalise); // order is semantic
    if (node !== null && typeof node === 'object') {
        const out = {};
        for (const k of Object.keys(node).sort()) {
            const v = node[k];
            if (v === undefined)
                continue;
            out[k] = canonicalise(v);
        }
        return out;
    }
    if (typeof node === 'number') {
        if (!Number.isFinite(node))
            throw new RangeError('Non-finite number in assumption content');
        return Object.is(node, -0) ? 0 : node;
    }
    return node;
}
/**
 * The exact string that is hashed. FINANCIAL content only: `values`,
 * `profileVersions`, `baseVersionId` and the schema version. Evidence and
 * comments are excluded so a harmless note never reads as a change to the
 * figures (§7.2, §10.3); expression modes ARE included even where two modes
 * resolve to the same number today.
 */
export function canonicalFinancialContent(content) {
    return JSON.stringify(canonicalise({
        canon: ASSUMPTION_CANONICALISATION_VERSION,
        schemaVersion: content.schemaVersion,
        baseVersionId: content.baseVersionId,
        values: content.values,
        profileVersions: [...content.profileVersions].sort((a, b) => a.profileId < b.profileId ? -1 : a.profileId > b.profileId ? 1 : 0),
    }));
}
function kindAllowed(def, v) {
    switch (v.kind) {
        case 'literal':
            return def.type === 'RATE' || def.type === 'SHARE';
        case 'money':
            return def.type === 'MONEY';
        case 'count':
            return def.type === 'AGE' || def.type === 'COUNT';
        case 'bool':
            return def.type === 'BOOL';
        case 'enum':
            return def.type === 'ENUM';
        case 'text':
            return def.type === 'TEXT';
        // A REF is how an index names the index it follows, so the field that
        // accepts one is either declared REF or is an ENUM base that may instead
        // point at another index (§5.1 `indices.{id}.base`).
        case 'ref':
            return def.type === 'REF' || parseIndexKey(def.key)?.field === 'base';
        case 'indexPlus':
            return def.indexLinkAllowed;
        case 'path':
            return def.pathAllowed;
    }
}
function literalNumbers(v) {
    switch (v.kind) {
        case 'literal':
            return [v.valueBps];
        case 'money':
            return [v.amountMinor];
        case 'count':
            return [v.count];
        case 'path':
            return v.segments.flatMap((s) => (s.value.kind === 'literal' ? [s.value.valueBps] : []));
        default:
            return [];
    }
}
function indexRefs(v) {
    if (v.kind === 'indexPlus')
        return [v.indexKey];
    if (v.kind === 'path') {
        return v.segments.flatMap((s) => (s.value.kind === 'indexPlus' ? [s.value.indexKey] : []));
    }
    return [];
}
/** Human display of a bound in the definition's unit — for messages only. */
function showBound(def, n) {
    if (def.unit === 'bps') {
        const sign = n < 0 ? '-' : '';
        const abs = Math.abs(n);
        return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}%`;
    }
    return String(n);
}
/**
 * First reference cycle reachable from `start`, as the full chain
 * (A -> B -> A), or null. `edges` maps a key to the keys it references.
 */
function findCycle(start, edges) {
    const stack = [];
    const onStack = new Set();
    const done = new Set();
    const visit = (k) => {
        if (onStack.has(k))
            return [...stack.slice(stack.indexOf(k)), k];
        if (done.has(k))
            return null;
        stack.push(k);
        onStack.add(k);
        for (const next of edges.get(k) ?? []) {
            const found = visit(next);
            if (found !== null)
                return found;
        }
        stack.pop();
        onStack.delete(k);
        done.add(k);
        return null;
    };
    return visit(start);
}
/**
 * `effective` is the content AFTER base inheritance has been applied (the
 * caller flattens the base chain; a standalone set passes its own values).
 * `own` is the draft's own values, used only for the coverage summary.
 */
export function validateAssumptionContent(effective, own = effective) {
    const errors = [];
    const warnings = [];
    const coverage = {};
    const bucket = (category) => (coverage[category] ??= { custom: 0, inherited: 0, missing: 0, invalid: 0 });
    const edges = new Map();
    for (const [key, v] of Object.entries(effective)) {
        // A collection instance (`indices.<id>.spread`) is governed by its
        // template; a TEMPLATE key is not storable content at all.
        const def = parseTemplateKey(key) === null ? assumptionDefinitionFor(key) : undefined;
        if (def === undefined) {
            // Never dropped silently (§4.1): an unknown active field blocks use.
            errors.push({
                key,
                code: 'unknown-key',
                message: `“${key}” is not an assumption this version of the product understands. Remove it, or upgrade before using this set.`,
            });
            continue;
        }
        if (def.status === 'gated') {
            errors.push({
                key,
                code: 'gated-capability',
                message: `${def.label} needs a modelling capability that is not available yet, so it cannot be set.`,
            });
            bucket(def.category).invalid += 1;
            continue;
        }
        if (!kindAllowed(def, v)) {
            errors.push({
                key,
                code: v.kind === 'path' || v.kind === 'indexPlus' ? 'not-allowed' : 'wrong-kind',
                message: v.kind === 'path'
                    ? `${def.label} cannot vary over time in the selected model. Enter a single figure.`
                    : v.kind === 'indexPlus'
                        ? `${def.label} cannot be linked to an index in the selected model. Enter a single figure.`
                        : `${def.label} expects a ${def.type} value.`,
            });
            bucket(def.category).invalid += 1;
            continue;
        }
        let bad = false;
        for (const n of literalNumbers(v)) {
            if (n < def.hardBounds.min || n > def.hardBounds.max) {
                errors.push({
                    key,
                    code: 'out-of-bounds',
                    message: `${def.label} must be between ${showBound(def, def.hardBounds.min)} and ${showBound(def, def.hardBounds.max)}.`,
                });
                bad = true;
                break;
            }
            if (def.softBounds !== undefined && (n < def.softBounds.min || n > def.softBounds.max)) {
                warnings.push({
                    key,
                    code: 'outside-plausible-range',
                    message: `${def.label} is outside the usual range of ${showBound(def, def.softBounds.min)} to ${showBound(def, def.softBounds.max)}. Check the figure, or record why it is intended.`,
                });
            }
        }
        const refs = indexRefs(v);
        for (const ref of refs) {
            if (assumptionDefinition(ref) === undefined) {
                errors.push({
                    key,
                    code: 'unknown-index',
                    message: `${def.label} is linked to “${ref}”, which does not exist. Choose another index or enter a figure.`,
                });
                bad = true;
            }
        }
        edges.set(key, refs);
        if (bad)
            bucket(def.category).invalid += 1;
    }
    const reported = new Set();
    for (const key of edges.keys()) {
        const cycle = findCycle(key, edges);
        if (cycle === null)
            continue;
        const id = [...cycle].sort().join('|');
        if (reported.has(id))
            continue;
        reported.add(id);
        const labels = cycle.map((k) => assumptionDefinition(k)?.label ?? k);
        errors.push({
            key: cycle[0],
            code: 'cycle',
            message: `${labels.join(' refers to ')}. Remove one of these links.`,
        });
    }
    for (const def of assumptionDefinitions()) {
        if (def.status !== 'active')
            continue;
        // A `{id}` template is not a key a set holds; its instances are checked
        // as a collection below, where "required" means required PER INDEX.
        if (parseTemplateKey(def.key) !== null)
            continue;
        const b = bucket(def.category);
        if (own[def.key] !== undefined)
            b.custom += 1;
        else if (effective[def.key] !== undefined)
            b.inherited += 1;
        /**
         * `REQ` with a `when` is CONDITIONALLY required — `economy.rpiSpread` is
         * needed only on the derived basis — so the condition, not this loop,
         * decides. The economy validator below reports it on the right field.
         */
        const required = def.defaultRule.kind === 'LEGACY' ||
            (def.defaultRule.kind === 'REQ' && def.defaultRule.when === undefined);
        // RPI derived from CPI is a figure this set DOES hold — as a basis and a
        // spread rather than a number of its own (§5.1 `economy.rpiLinkMode`).
        const derivedRpi = def.key === 'economy.rpi' &&
            effective['economy.rpiLinkMode']?.kind === 'enum' &&
            effective['economy.rpiLinkMode'].option === 'linked';
        if (required && !derivedRpi && effective[def.key] === undefined) {
            b.missing += 1;
            errors.push({
                key: def.key,
                code: 'missing-required',
                message: `${def.label} has no figure. Enter one before this set can be published or used.`,
            });
        }
    }
    /**
     * The §5.1 economy group as a COLLECTION: each named index complete in
     * itself, names unique, references resolvable, no cycles, and RPI's basis
     * consistent. Run through the same resolver the API pins with and the editor
     * previews with, so all three agree about what is publishable.
     */
    const cpi = effective['economy.cpi'];
    const earnings = effective['economy.earnings'];
    const storedRpi = effective['economy.rpi'];
    const indexKeys = Object.keys(effective).filter((k) => parseIndexKey(k) !== null);
    const economyTouched = indexKeys.length > 0 ||
        effective['economy.rpiLinkMode'] !== undefined ||
        effective['economy.rpiSpread'] !== undefined;
    if (economyTouched && cpi?.kind === 'literal' && earnings?.kind === 'literal') {
        const resolution = resolveEconomy(effective, {
            cpiBps: cpi.valueBps,
            storedRpiBps: storedRpi?.kind === 'literal' ? storedRpi.valueBps : undefined,
            earningsBps: earnings.valueBps,
        });
        if (!resolution.ok) {
            for (const issue of resolution.errors) {
                // A gated field has already been refused above; do not say it twice.
                if (assumptionDefinitionFor(issue.key)?.status === 'gated')
                    continue;
                errors.push({
                    key: issue.key,
                    code: issue.code,
                    message: issue.message,
                });
                bucket('economy').invalid += 1;
            }
        }
        else {
            for (const index of resolution.economy.indices) {
                bucket('economy').custom += 1;
                const def = assumptionDefinitionFor(`indices.${index.id}.rate`);
                if (def.softBounds !== undefined &&
                    (index.rateBps < def.softBounds.min || index.rateBps > def.softBounds.max)) {
                    warnings.push({
                        key: `indices.${index.id}.rate`,
                        code: 'outside-plausible-range',
                        message: `${index.name} resolves to a rate outside the usual range. Check the figure, or record why it is intended.`,
                    });
                }
            }
        }
    }
    return { errors, warnings, coverage };
}
//# sourceMappingURL=content.js.map